import { useEffect, useMemo } from 'react';
import { Grid, Typography } from '@mui/material';
import { theme } from 'theme';
import { jbColors } from 'theme/jbColors';
import { useDispatch, useSelector } from 'react-redux';
import { selectUserFeatureToggles } from 'selectors/userFeatureToggleSelector';
import ClientPricingPortal from 'components/productsAndServicesOverviewCards/ClientPricingPortal';
import AddProduct from 'components/productsAndServicesOverviewCards/AddProduct';
import JBMobileInsightsAccess from 'components/productsAndServicesOverviewCards/JBMobileInsightsAccess';
import CreateMandateProposal from 'components/productsAndServicesOverviewCards/CreateMandateProposal';
import ViewMandateProposal from 'components/productsAndServicesOverviewCards/ViewMandateProposal';
import VideoIdentification from 'components/productsAndServicesOverviewCards/VideoIdentification';
import SubscriptionManagement from 'components/productsAndServicesOverviewCards/SubscriptionManagement';
import CloseAllProducts from 'components/productsAndServicesOverviewCards/CloseAllProducts';
import { AppDispatch, RootState } from 'store';
import { LoadingSpinner } from 'components/loadingSpinner/LoadingSpinner';
import { determineStatusFromEnum } from 'types';
import { ServiceAgreementV2 } from 'pco-product-service-internal-consumer';
import { setAgreementsToClose } from 'reducers/serviceAgreementsReducer';
import { useLocation } from 'react-router';
import EamEfaDashboard from '../../components/productsAndServicesOverviewCards/EamEfaDashboard';

const styles = {
  container: {
    height: '100vh',
    backgroundColor: jbColors.core.stone[30],
    marginTop: '-16px',
    paddingTop: theme.spacing(3),
  },
  innerContainer: {
    display: 'grid',
    paddingTop: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    gridTemplateColumns: 'repeat(3, 14vw)',
    gridGap: theme.spacing(2),
    minWidth: '14vw',
    backgroundColor: jbColors.core.stone[30],
  },
};

export const cardStyle = {
  minWidth: '14vw',
  flexDirection: 'column',
  display: 'flex',
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(1),
  marginLeft: '0.2em',
  justifyContent: 'space-between',
};

type OrderNewProductOrServiceProps = {
  brUid?: string | null;
  partnerNumber?: string | null;
};

export const OrderNewProductOrService = ({
  brUid: brUidRaw,
  partnerNumber: partnerNumberRaw,
}: OrderNewProductOrServiceProps) => {
  const brUid = brUidRaw ?? undefined;
  const partnerNumber = partnerNumberRaw ?? undefined;
  const features = useSelector(selectUserFeatureToggles);
  const loader = useSelector((state: RootState) => state.appConfig.loader);
  const agreements = useSelector((state: RootState) => state.serviceAgreements.tableData);
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();

  const agreementsToClose = useMemo<string[]>(
    () =>
      agreements
        .filter(
          (agreement: ServiceAgreementV2) =>
            determineStatusFromEnum(agreement.agreementStatus) === 'In Opening' ||
            determineStatusFromEnum(agreement.agreementStatus) === 'Active',
        )
        .map((agreement: ServiceAgreementV2) => agreement.serviceAgreementId)
        .filter((id): id is string => !!id),
    [agreements],
  );

  useEffect(() => {
    if (agreements.length > 0) {
      dispatch(setAgreementsToClose(agreementsToClose));
    }
  }, [agreements.length, agreementsToClose, dispatch]);

  return (
    <Grid data-testid="order-product-service" sx={styles.container}>
      <Typography
        style={{
          paddingBottom: theme.spacing(2),
          paddingLeft: theme.spacing(2.5),
        }}
        variant="h1"
      >
        Actions
      </Typography>
      {loader?.loading ? (
        <LoadingSpinner message={loader.message} />
      ) : (
        <Grid sx={styles.innerContainer}>
          {((features.BUSINESS_RELATION?.AddProducts && brUid) || (features.PARTNER?.AddProducts && partnerNumber)) && (
            <AddProduct partnerId={partnerNumber} brUid={brUid} />
          )}
          {!location.pathname.includes('partner') && (
            <CloseAllProducts partnerId={partnerNumber} agreementsToClose={agreementsToClose} />
          )}
          {((features.BUSINESS_RELATION?.ClientPricingPortal && brUid) ||
            (features.PARTNER?.ClientPricingPortal && partnerNumber)) && <ClientPricingPortal />}
          {((features.BUSINESS_RELATION?.VideoIdentification && brUid) ||
            (features.PARTNER?.VideoIdentification && partnerNumber)) && (
            <VideoIdentification partnerId={partnerNumber} />
          )}
          {((features.BUSINESS_RELATION?.JBMobileInsightsAccess && brUid) ||
            (features.PARTNER?.JBMobileInsightsAccess && partnerNumber)) && (
            <JBMobileInsightsAccess partnerId={partnerNumber} />
          )}
          {((features.BUSINESS_RELATION?.CreateMandateProposal && brUid) ||
            (features.PARTNER?.CreateMandateProposal && partnerNumber)) && (
            <CreateMandateProposal partnerId={partnerNumber} brUid={brUid} />
          )}
          {((features.BUSINESS_RELATION?.ViewMandateProposal && brUid) ||
            (features.PARTNER?.ViewMandateProposal && partnerNumber)) && (
            <ViewMandateProposal partnerId={partnerNumber} brUid={brUid} />
          )}
          {((features.BUSINESS_RELATION?.SubscriptionManagement && brUid) ||
            (features.PARTNER?.SubscriptionManagement && partnerNumber)) && (
            <SubscriptionManagement partnerId={partnerNumber} />
          )}
          {((features.BUSINESS_RELATION?.EamEfaDashboard && brUid) ||
            (features.PARTNER?.EamEfaDashboard && partnerNumber)) && <EamEfaDashboard partnerId={partnerNumber} />}
        </Grid>
      )}
    </Grid>
  );
};
